import { useMemo } from 'react'

import { Button } from 'src/shared/components/ui/buttons/button'
import { cn } from 'src/shared/utils/cn'

import { useUserById } from '../../../api/accounts/use-user-by-id'
import { useUserReviews } from '../../../api/marketplace/use-user-reviews'
import type { Expertise } from '../../../schemas/expertise'
import { expertiseMap } from '../../../schemas/expertise'
import type { Language } from '../../../schemas/language'
import { languageMap } from '../../../schemas/language'
import type { Profession } from '../../../schemas/profession'
import { CoverPhotoFallback } from '../../profile-photos/cover-photo'
import MultiProfilePhoto from '../../profile-photos/multi-profile-photo'
import ReviewNumber from '../reviews/review-number'

import Building1 from './assets/building-1.webp'
import Building2 from './assets/building-2.webp'
import Building3 from './assets/building-3.webp'
import { BuildingIcon } from './assets/building-icon'
import { CalendarIcon } from './assets/calendar-icon'
import { CrownIcon } from './assets/crown-icon'
import { LanguageIcon } from './assets/language-icon'
import { MapPinIcon } from './assets/map-pin-icon'
import WritingOnCurvedPaper from './assets/writing-on-curved-paper.png'

interface ProfileOverviewProps {
  userId: number
}

export default function ProfileOverview({ userId }: ProfileOverviewProps) {
  const { data: userData } = useUserById(userId)
  const { data: reviewsData } = useUserReviews(userId)

  // TODO: TEMP DATA for reviews summary
  // const hasReviews = !!reviewsData
  const averageRating = reviewsData?.averageRating || undefined
  const totalReviews = reviewsData?.totalReviews || 0

  // Data for user summary
  const careerStartDate = useMemo(() => {
    if (!userData?.careerStartDate) return null
    const date = new Date(userData.careerStartDate)
    if (isNaN(date.getTime())) {
      return null
    }
    return date.getFullYear()
  }, [userData?.careerStartDate])

  // Data for listings summary
  const listingsSummary = useMemo(() => {
    return {
      totalVolume: '$5,653,000',
      transactions: 14,
      avgSize: '$402,500',
      referred: '$2,070,000 (6)',
      numberOfActiveListings: 3,
      photo1: Building1,
      photo2: Building2,
      photo3: Building3,
    }
  }, [])

  const showListingsSummary = userData?.username === 'leo'
  // const showListingsSummary = true

  const firstReview = useMemo(() => {
    return reviewsData?.reviews[0]
  }, [reviewsData])

  const expertiseList = useMemo(() => {
    return userData?.expertise
      ?.map((e) => expertiseMap[userData?.profession as unknown as Profession][e as unknown as Expertise]?.label)
      .filter(Boolean)
  }, [userData])

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-[20px] md:grid-cols-3 lg:gap-[25px]',
        !showListingsSummary && 'md:grid-cols-2',
      )}
    >
      {/* Review Summary */}
      <div className="flex flex-col gap-[12px] rounded-[20px] bg-[#F8F7F5] px-[24px] py-[30px] xs:gap-[15px] xs:px-[25px] xs:py-[35px]">
        {firstReview ? (
          <>
            <div className="text-[16px] font-semibold tracking-[-0.18px] text-primary xs:text-[18px]">
              People are saying
            </div>
            {averageRating && <ReviewNumber rating={averageRating} />}
            <div className="mt-2 line-clamp-5 text-[14px] font-medium leading-[1.6] text-primary xs:text-[15px]">
              {firstReview.description ||
                "Closed a deal today where the client didn't know their credit score until last month. A little guidance goes a long way—feels good to help people"}
              {/* <div className="mt-[6px] text-[11px] italic text-muted">Summary made by Lynk</div> */}
            </div>
            {totalReviews > 0 && (
              <div className="flex flex-row items-center gap-2">
                <MultiProfilePhoto profilePhotos={[]} size={40} />
                <span className="text-[11px] text-muted">
                  Based on{' '}
                  <span className="text-primary underline">{`${totalReviews} review${
                    totalReviews > 1 ? 's' : ''
                  }`}</span>
                </span>
              </div>
            )}
            <Button className="mt-3 h-[45px] w-full rounded-full bg-primary text-[14px] font-semibold text-white xs:w-[155px]">
              Show all reviews
            </Button>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2">
            <div className="flex size-[136px] items-center justify-center">
              <img src={WritingOnCurvedPaper} className="" />
            </div>
            <p className="max-w-[263px] text-center text-[18px] font-semibold leading-normal tracking-[-0.18px] text-primary">{`${userData?.firstName} doesn't have reviews at the moment`}</p>
          </div>
        )}
      </div>
      {/* User Summary */}
      <div className="flex flex-col gap-1 rounded-[20px] bg-[#F8F7F5] px-[24px] py-[30px] xs:gap-[15px] xs:px-[25px] xs:py-[35px]">
        <div className="text-[18px] font-semibold tracking-[-0.18px] text-primary">About {userData?.firstName}</div>
        <div className="mt-4 flex flex-col gap-[12px] xs:gap-[14px]">
          {userData?.shortAddress && (
            <UserSummaryListItem icon={<MapPinIcon size={13} />} label={userData.shortAddress} />
          )}
          {careerStartDate && (
            <UserSummaryListItem icon={<CalendarIcon size={15} />} label={`Agent since ${careerStartDate}`} />
          )}
          {expertiseList && expertiseList.length > 0 && (
            <UserSummaryListItem icon={<CrownIcon size={15} />} label={`Expert in ${expertiseList.join(` · `)}`} />
          )}
          {userData?.languages && userData.languages.length > 0 && (
            <UserSummaryListItem
              icon={<LanguageIcon size={17} />}
              label={`Speaks ${userData.languages
                .map((e) => languageMap[e as unknown as Language]?.label)
                .join(` · `)}`}
            />
          )}
          {userData?.companyName && (
            <UserSummaryListItem icon={<BuildingIcon size={15} />} label={`Works at ${userData?.companyName}`} />
          )}
        </div>
      </div>

      {/* Listings Summary */}
      {showListingsSummary && (
        <div className="flex flex-col gap-[15px] rounded-[20px] bg-[#F8F7F5] px-[24px] py-[30px] xs:px-[25px] xs:py-[35px]">
          <ListingsSummaryPhotos
            photo1={listingsSummary.photo1}
            photo2={listingsSummary.photo2}
            photo3={listingsSummary.photo3}
          />
          <div className="py-3 text-center text-[22px] font-semibold tracking-[-0.22px] text-primary">
            {listingsSummary.numberOfActiveListings} active listings
          </div>
          <div className="h-px shrink-0 bg-[#D8D8D6]"></div>
          <div className="flex flex-1 flex-col justify-between gap-[12px]">
            <div className="flex flex-col gap-2 text-[13px] tracking-[-0.13px] text-primary">
              <div className="flex flex-row justify-between">
                <span>Total volume</span>
                <span>{listingsSummary.totalVolume}</span>
              </div>
              <div className="flex flex-row justify-between">
                <span>Transactions</span>
                <span>{listingsSummary.transactions}</span>
              </div>
              <div className="flex flex-row justify-between">
                <span>Avg size</span>
                <span>{listingsSummary.avgSize}</span>
              </div>
              <div className="flex flex-row justify-between">
                <span>Referred</span>
                <span>{listingsSummary.referred}</span>
              </div>
            </div>
            <p className="text-[11px] leading-[1.6] text-muted">Only reflects transactions made on Conrad</p>
          </div>
        </div>
      )}
    </div>
  )
}

function UserSummaryListItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex flex-row items-center gap-3">
      <span className="flex size-[35px] shrink-0 items-center justify-center rounded-full bg-white">{icon}</span>
      <span className="text-[15px] tracking-[-0.15px] text-primary">{label}</span>
    </div>
  )
}

function ListingsSummaryPhotos({ photo1, photo2, photo3 }: { photo1: string; photo2: string; photo3: string }) {
  return (
    <div className="relative flex flex-row items-center justify-center">
      <div className="h-[100px] w-[120px] shrink-0 translate-x-3/4 translate-y-[2px] -rotate-6 rounded-[20px] border-[3px] border-white shadow-[0_3px_15px_1px_rgba(27,48,41,0.20)] lg:translate-x-[60%]">
        <img src={photo2 || CoverPhotoFallback} className="size-full rounded-[20px] object-cover" />
      </div>
      <div className="z-[5] h-[108px] w-[130px] shrink-0 rounded-[20px] border-[3px] border-white shadow-[0_3px_15px_1px_rgba(27,48,41,0.20)]">
        <img src={photo1 || CoverPhotoFallback} className="size-full rounded-[20px] object-cover" />
      </div>
      <div className="h-[100px] w-[120px] shrink-0 -translate-x-3/4 translate-y-[2px] rotate-6 rounded-[20px] border-[3px] border-white shadow-[0_3px_15px_1px_rgba(27,48,41,0.20)] lg:translate-x-[-60%]">
        <img src={photo3 || CoverPhotoFallback} className="size-full rounded-[20px] object-cover" />
      </div>
    </div>
  )
}
